import React, { useState, useEffect, useRef } from "react";
import { useTheme } from "../ThemeContext";
import { useNavigate } from "react-router-dom";
import { collection, getDocs } from "firebase/firestore";
import { getStorage, ref, getDownloadURL } from "firebase/storage";
import { db } from "../Config/config";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { CounterSection, Footer, Testimonials } from "./Footer";
import Partners from "./Partners";
import FeatureCarousel from "./FeatureCarousel";

const packageOrder = [
  "joyMars",
  "joyMercury",
  "joySun",
  "joyMoon",
  "joyJupiter",
  "joyVenus",
  "joyMilkyWay",
  "joyGalaxy",
];

const Packages = () => {
  const { themes, currentTheme } = useTheme();
  const theme = themes[currentTheme];
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [displayedText, setDisplayedText] = useState("");
  const scrollContainerRef = useRef(null);

  const isMobile = windowWidth <= 768;
  const fullText = "TRAVEL MADE AFFORDABLE";

  const getImageNumber = (packageName) => {
    const nameToImageMap = {
      "JOY GALAXY": 1,
      "JOY JUPITER": 2,
      "JOY MILKY WAY": 3,
      "JOY VENUS": 4,
      "JOY MARS": 5,
      "JOY MERCURY": 6,
      "JOY MOON": 7,
      "JOY SUN": 8,
    };

    for (const [name, number] of Object.entries(nameToImageMap)) {
      if (packageName.toUpperCase().includes(name)) {
        return number;
      }
    }
    return 1;
  };

  const handlePackageClick = (packageId) => {
    navigate(`/packagedetails/${packageId}`);
  };

  const handleScroll = (direction) => {
    const container = scrollContainerRef.current;
    if (container) {
      const scrollAmount =
        direction === "left" ? -container.offsetWidth : container.offsetWidth;
      container.scrollBy({ left: scrollAmount, behavior: "smooth" });
    }
  };

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    let typingInterval;

    if (isMobile) {
      let index = 0;
      typingInterval = setInterval(() => {
        if (index <= fullText.length) {
          setDisplayedText(fullText.slice(0, index));
          index++;
        } else {
          setTimeout(() => {
            index = 0;
            setDisplayedText("");
          }, 1000);
        }
      }, 200);
    }

    return () => {
      clearInterval(typingInterval);
    };
  }, [isMobile, fullText]);

  useEffect(() => {
    const cacheDuration = 30 * 60 * 1000; // 30 minutes in milliseconds
    const cachedData = localStorage.getItem('packagesCache');

    if (cachedData) {
      const { data, timestamp } = JSON.parse(cachedData);
      const isCacheValid = (Date.now() - timestamp) < cacheDuration;

      if (isCacheValid) {
        setPackages(data);
        setIsLoading(false);
        return;
      }
    }

    const fetchPackages = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const packagesCollection = collection(db, "packages");
        const packagesSnapshot = await getDocs(packagesCollection);

        if (packagesSnapshot.empty) {
          setError("No packages available at the moment.");
          setIsLoading(false);
          return;
        }

        const storage = getStorage();
        const allPackages = await Promise.all(
          packagesSnapshot.docs.map(async (doc) => {
            const data = doc.data();
            const imageNumber = getImageNumber(data.name);
            const imagePath = `package-images/${imageNumber}.jpeg`;
            const imageRef = ref(storage, imagePath);
            const imageUrl = await getDownloadURL(imageRef);

            return {
              id: doc.id,
              ...data,
              image: imageUrl,
            };
          })
        );

        // Sort packages based on the predefined order
        const sortedPackages = allPackages.sort((a, b) => {
          const indexA = packageOrder.indexOf(a.id);
          const indexB = packageOrder.indexOf(b.id);
          return indexA - indexB;
        });

        setPackages(sortedPackages);
        setIsLoading(false);

        // Cache the data
        localStorage.setItem('packagesCache', JSON.stringify({ data: sortedPackages, timestamp: Date.now() }));
      } catch (err) {
        console.error("Error fetching packages:", err);
        setError("Failed to load packages. Please try again later.");
        setIsLoading(false);
      }
    };

    fetchPackages();
  }, []);

  if (error) return <ErrorMessage>{error}</ErrorMessage>;

  return (
    <>
      <Container backgroundColor={theme.bgColor}>
        {isMobile && (
          <>
            <MobileHeader>
              <HeaderText>
                {fullText.split(" ").map((word, wordIndex, wordArray) => (
                  <React.Fragment key={wordIndex}>
                    <HeaderWord isAffordable={word === "AFFORDABLE"}>
                      {displayedText.slice(
                        fullText.indexOf(word),
                        fullText.indexOf(word) + word.length
                      )}
                    </HeaderWord>
                    {wordIndex < wordArray.length - 1 && " "}
                  </React.Fragment>
                ))}
              </HeaderText>
            </MobileHeader>
            <FeatureCarousel />
          </>
        )}
        <Title color={theme.textColor}>Packages</Title>

        <ScrollContainer>
          <ScrollButton onClick={() => handleScroll("left")} position="left">
            <ChevronLeft size={24} />
          </ScrollButton>
          <PackageGrid ref={scrollContainerRef} isMobile={isMobile}>
            {packages.map((pkg) => (
              <PackageCard
                key={pkg.id}
                onClick={() => handlePackageClick(pkg.id)}
              >
                <PackageName windowWidth={windowWidth}>{pkg.name}</PackageName>
                <PackageImage src={pkg.image} alt={pkg.name} />
                <Overlay>
                  <ViewDetailsButtonWrapper>
                    <ViewDetailsButton to={`/packagedetails/${pkg.id}`}>
                      GO NOW
                    </ViewDetailsButton>
                  </ViewDetailsButtonWrapper>
                </Overlay>
              </PackageCard>
            ))}
          </PackageGrid>
          <ScrollButton onClick={() => handleScroll("right")} position="right">
            <ChevronRight size={24} />
          </ScrollButton>
        </ScrollContainer>
      </Container>
      <CounterSection />
      <Partners />
      <Testimonials />
      <Footer />
    </>
  );
};

const Container = styled.div`
  padding: ${(props) =>
    props.windowWidth <= 330
      ? "10px"
      : props.windowWidth <= 768
      ? "15px"
      : "15px"};
  background-color: ${(props) => props.backgroundColor};
  font-family: "Poppins", sans-serif;
`;

const MobileHeader = styled.div`
  display: flex;
  background-color: #441B77;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100vw;
  height: 40vh;
  padding: 20px 0;
  box-sizing: border-box;
  overflow: hidden;
  position: relative;
  left: 50%;
  right: 50%;
  margin-left: -50vw;
  margin-right: -50vw;
  margin-top: -10vw;
`;

const HeaderText = styled.div`
  color: #f8e1b2;
  font-size: 22px;
  font-weight: bold;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  border-right: 0.15em solid #f8e1b2;
  max-width: 100%;
  margin: 0 auto;
  margin-bottom: -5vh;
  padding-bottom: 20px; // Use padding instead of negative margin
  letter-spacing: 0.05em;
  font-family: "Poppins", sans-serif;

  @media (max-width: 480px) {
    font-size: 18px;
    padding-bottom: 15px;
  }

  @media (max-width: 330px) {
    font-size: 16px;
    padding-bottom: 10px;
  }
`;

const HeaderWord = styled.span`
  display: inline-block;
  background-color: ${(props) =>
    props.isAffordable ? "white" : "transparent"};
  color: ${(props) => (props.isAffordable ? "#441B77" : "#F8E1B2")};
  padding: ${(props) => (props.isAffordable ? "0 5px" : "0")};
  border-radius: ${(props) => (props.isAffordable ? "4px" : "0")};
`;

const Title = styled.h1`
  font-size: 2.5rem;
  color: ${(props) => props.color};
  text-align: center;
  margin-bottom: 40px;

  @media (max-width: 480px) {
    font-size: 2rem;
    margin-bottom: 30px;
  }

  @media (max-width: 330px) {
    font-size: 1.8rem;
    margin-bottom: 20px;
  }
`;

const ScrollContainer = styled.div`
  position: relative;
  width: 100%;
  overflow: hidden;
  margin-bottom: 40px;

  @media (min-width: 769px) {
    display: flex;
    justify-content: center;
  }
`;

const PackageGrid = styled.div`
  display: flex;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }

  @media (max-width: 768px) {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
    justify-content: center;  /* Center the grid */
    align-items: center;      /* Center vertically */
    overflow-x: visible;
    padding: 0 12px;
    width: 100%;
    max-width: 100%;
    margin: 0 auto;
  }

  @media (max-width: 330px) {
    gap: 8px;
    padding: 0 10px;
  }

  @media (min-width: 769px) {
    max-width: 1200px;
    justify-content: center;
    flex-wrap: wrap;
    gap: 15px;
    display: grid;
    grid-template-columns: repeat(4, 1fr);
  }
`;

const PackageCard = styled.div`
  flex: 0 0 calc(25% - 20px);
  cursor: pointer;
  transition: transform 0.3s ease;
  background-color: white;
  border-radius: 12px;
  border-width: 8px;
  border-style: solid;
  border-color: #441B77;
  overflow: hidden;
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
  position: relative;

  /* Mobile Styles */
  @media (max-width: 768px) {
    height: 38vh;
    width: 46vw;  /* Increased from 42vw */
    min-height: 300px;
    border-width: 4px;
    margin: 0 auto;  /* Center individual cards */
    justify-self: center;  /* Center in grid */
    
  }

  @media (max-width: 480px) {
    height: 35vh;
    min-height: 270px;
    width: 44vw;  /* Increased from 40vw */
    border-width: 4px;
  }

  @media (max-width: 330px) {
    height: 32vh;
    min-height: 250px;
    width: 43vw;  /* Increased from 39vw */
    border-width: 4px;
  }

  /* Desktop Styles */
  @media (min-width: 769px) {
    height: 450px;
    margin-right: 0;
    flex: 0 0 calc(25% - 15px);
    border-width: 4px;
    max-width: 300px;
  }

  &:hover {
    transform: translateY(-5px);
  }
`;

const PackageName = styled.div`
  position: absolute;
  top: 10px;
  left: 50%;
  transform: translateX(-50%);
  color: #441B77;
  font-weight: bold;
  background-color: white;
  padding: 5px 10px;
  border-radius: 4px;
  z-index: 2;
  font-size: ${(props) => (props.windowWidth <= 768 ? "0.8rem" : "1.2rem")};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 90%;

  @media (max-width: 480px) {
    font-size: 0.7rem;
    padding: 4px 8px;
    top: 5px;
  }

  @media (max-width: 330px) {
    font-size: 0.65rem;
    padding: 3px 6px;
    top: 5px;
  }

  @media (max-width: 285px) {
    font-size: 0.45rem;
    padding: 3px 6px;
    top: 5px;
  }
`;

const PackageImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 20px;

  @media (max-width: 480px) {
    padding: 10px;
  }

  @media (max-width: 330px) {
    padding: 8px;
  }
`;

const ViewDetailsButtonWrapper = styled.div`
  position: absolute;
  bottom: 20px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;

  @media (max-width: 480px) {
    bottom: 10px;
  }
  @media (max-width: 330px) {
    bottom: 8px;
  }
`;

const ViewDetailsButton = styled(Link)`
  background-color: #441B77;
  color: white;
  border: 2px solid white;
  padding: 8px 16px;
  border-radius: 5px;
  cursor: pointer;
  text-transform: uppercase;
  font-size: 100%;
  text-decoration: none;
  display: inline-block;
  text-align: center;
  transition: background-color 0.3s;

  @media (max-width: 480px) {
    padding: 6px 12px;
    font-size: 80%;
  }

  @media (max-width: 330px) {
    padding: 5px 10px;
    font-size: 70%;
  }
`;

const ErrorMessage = styled.div`
  color: red;
  text-align: center;
  font-size: 1.2rem;
  margin-top: 20px;

  @media (max-width: 480px) {
    font-size: 1rem;
    margin-top: 15px;
  }

  @media (max-width: 330px) {
    font-size: 0.9rem;
    margin-top: 10px;
  }
`;

const ScrollButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background-color: rgba(255, 255, 255, 0.7);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  z-index: 10;
  ${(props) => props.position}: 10px;

  &:hover {
    background-color: rgba(255, 255, 255, 0.9);
  }

  @media (max-width: 768px) {
    display: none;
  }

  @media (min-width: 769px) {
    ${(props) => props.position}: -50px;
  }
`;

export default Packages;